// @ts-nocheck
import { findClosestPoints, findLinesIntersection, findRectangleAndLineIntersectionPoints, getLineSelectBoundary, getProjection, getSideOfMouseRelativeToLine, isPointInsideFrame, isinSelectBoundaryLine, pointInsideRectangle, roundPoint, transformPointByMatrix3, getDistance } from "../../shared/common";
import { BasicShape } from "../BasicShape";
import { Point } from "../Point";
import { mat3 } from "gl-matrix";
import { Guide } from "./Guide";


import { s } from "../../shared/globalState/settings";
import { Join } from "../Join";
import { ductOrPipe, NetworkInfo } from "../NetworkInfo";
import { Vector } from "../Vector";
import { ObjectTypes } from "../../shared/enums";

import { Grip } from "../snaps/Grip";
import { TripH } from "../snaps/TripH";
import { TripV } from "../snaps/TripV";

import { getDisplayedShapes, a } from "../../shared/globalState/a";
import { RouteCrucialPoints, Shape } from "../../shared/types";

import { isin } from "../../shared/common";
import { ElbowBlock } from "../../models/resistances/ElbowBlock";
import { DrawingModes } from "../../shared/enums";

export class Line extends BasicShape {
    isSelected: boolean;

    private _start: any;
    private _end: any;
    mid: any;
    type: string;
    subtype: string;
    color: number[];
    thickness: any;
    startJ: Join;
    endJ: Join;

    routeId: string; // routeId используется только для линий, в режиме черчения линиями или наброска сети
    netId: string; // netId используется для линий и блоков, в режиме черчения сети воздуховодов (труб)
    
    net: NetworkInfo;
    joinedStart: Join;
    joinedEnd: Join;
    len: number;

    checked: boolean;

    get start() {
        return this._start;
    }
    set start(point: Point) {
        this.len = this.getLength();

        this._start = roundPoint(point);

        this.mid = this.getMid(this.start, this.end);

        // ********* обновление сохранённых вершин **********
        // this.saved = this.getVertices();
    }
    get end() {
        return this._end;
    }
    set end(point) {
        this.len = this.getLength();

        this._end = roundPoint(point);

        this.mid = this.getMid(this.start, this.end);
        // ********* обновление сохранённых вершин **********
        // this.saved = this.getVertices();
    }

    get segmentShape() {
        if (this.net.diameterMil) {
            return ObjectTypes.round;
        }
        else if (this.net.widthMil && this.net.widthMil) {
            return ObjectTypes.rect;
        }
    }



    constructor(start: Point, end: Point, color: number[], thickness) {
        /**
         * Конструктор - это функция, start и end - это входные данные
         * Функция не должна менять входные данные,
         * значит если входные данные являются неизменяемым типом,
         * то можно просто присвоить их,
         * а если входные данные - это объект или массив,
         * то нужно скопировать их, создав новый объект или массив
         */

        super();
        this.type = ObjectTypes.line;
        this._start = start ? start.clone() : null;

        this._end = end ? end.clone() : null;
        this.mid = this.getMid(start, end);
        this.color = [...color];
        this.thickness = thickness;

        this.startJ = new Join(this._start, super.id);
        this.endJ = new Join(this._end, super.id);

    }


    getMid(start, end) {
        const midX = (start.x + end.x) / 2;
        const midY = (start.y + end.y) / 2;
        return new Point(midX, midY);
    }

    /**
     * Updates the mid point coordinates based on current start and end positions.
     * Used by ElbowBlock to update line geometry while preserving Point references.
     */
    updateMid() {
        if (this.mid) {
            this.mid.x = (this._start.x + this._end.x) / 2;
            this.mid.y = (this._start.y + this._end.y) / 2;
        } else {
            this.mid = this.getMid(this._start, this._end);
        }
    }

    /**
     * Updates line endpoint coordinates directly, preserving Point references.
     * Used by ElbowBlock to maintain connection without creating new Point instances.
     */
    updateEndpoint(whichEnd: 'start' | 'end', newPoint: Point) {
        if (whichEnd === 'start') {
            this._start.x = newPoint.x;
            this._start.y = newPoint.y;
        } else {
            this._end.x = newPoint.x;
            this._end.y = newPoint.y;
        }
        this.updateMid();
    }

    getVertices() {
        return new Float32Array(this.getTriangulatedVertices());
    }
    getVerticesArray() {
        return [
            this.start.x, this.start.y,
            this.end.x, this.end.y,
        ];
    }

    getTriangulatedVertices() {
        let start = this.start;
        let end = this.end;
        let width = this.thickness;
        
        // In duct mode, use actual duct width/height from network info if available
        if (a.drawingMode === DrawingModes.duct && this.net) {
            // For rectangular ducts, use the larger dimension (width or height) as the visual width
            // This represents the duct cross-section perpendicular to the line
            if (this.net.widthMil && this.net.heightMil) {
                // Use the larger dimension to show the duct size
                // Convert from millimeters to pixels
                // Note: zlc is the zoom level, we should account for it
                const zoomFactor = a.zlc || 1;
                // Use the larger dimension to represent the duct visually
                // This shows the duct cross-section size
                width = Math.max(this.net.widthMil, this.net.heightMil) * zoomFactor;
            } else if (this.net.diameterMil) {
                // For round ducts, use diameter
                const zoomFactor = a.zlc || 1;
                width = this.net.diameterMil * zoomFactor;
            }
        }
        
        // Calculate the vector along the line segment
        let dx = end.x - start.x;
        let dy = end.y - start.y;

        // Calculate the normalized perpendicular vector
        let length = Math.hypot(dx, dy);
        if (length === 0) {
            // Avoid division by zero
            return [];
        }
        let nx = dy / length;  // Normalized perpendicular vector x
        let ny = -dx / length; // Normalized perpendicular vector y

        // Calculate the offset points for the rectangle corners
        let offsetX = nx * width / 2;
        let offsetY = ny * width / 2;

        // Calculate the four corners of the rectangle
        let corner1 = { x: start.x + offsetX, y: start.y + offsetY };
        let corner2 = { x: end.x + offsetX, y: end.y + offsetY };
        let corner3 = { x: end.x - offsetX, y: end.y - offsetY };
        let corner4 = { x: start.x - offsetX, y: start.y - offsetY };

        // Return the coordinates of the rectangle corners as two triangles
        return [
            corner1.x, corner1.y,
            corner2.x, corner2.y,
            corner3.x, corner3.y,
            corner1.x, corner1.y,
            corner3.x, corner3.y,
            corner4.x, corner4.y
        ];
    }




    /**
     * Очень удобно, что этот блок выполняется только когда черчение линии закончено (все линии создаются из глобальной a.line) - то есть длина уже определена.
     */
    getClone() {
        const line = new Line(this.start, this.end, this.color, this.thickness);

        line.rtype = this.rtype;

        if (this.net) {
            line.net = this.net.getClone();
        }

        /**
         * у линии всегда будут концы соединения и номер маршрута, так что эта функциональность является частью объекта Line.
         * В отличие от объекта NetInfo, который присоединяется только, если пользователь намерен использовать линию как воздуховод (трубу)
         * Поэтому создание объекта NetInfo перенесено в основное событие, где линия создаётся - в модуль down
         */
        line.routeId = this.routeId;
        line.startJ.id = this.startJ.id;
        line.endJ.id = this.endJ.id;

        // ********* обновление сохранённых вершин *********
        // line.saved = this.getVertices();
        return line;
    }


    getCloneGuide() {
        const guide = new Guide(this.start, this.end, this.color, this.thickness);
        guide.id = super.id;


        // ********* обновление сохранённых вершин *********
        // line.saved = this.getVertices();
        return guide;
    }

    isinSelectFrame(frame) {
        // Validate frame before proceeding
        if (!frame || !frame.point1 || !frame.point2 || !frame.point3) {
            return false;
        }
        
        const angle = Math.atan2(this.end.y - this.start.y, this.end.x - this.start.x);
        const offsetX = s.tolerance * Math.sin(angle);
        const offsetY = s.tolerance * Math.cos(angle);
        const point1 = new Point(this.start.x - offsetX, this.start.y + offsetY);
        const point2 = new Point(this.end.x - offsetX, this.end.y + offsetY);
        const point3 = new Point(this.end.x + offsetX, this.end.y - offsetY);
        const point4 = new Point(this.start.x + offsetX, this.start.y - offsetY);
        if (
            isPointInsideFrame(frame, point1.x, point1.y) &&
            isPointInsideFrame(frame, point2.x, point2.y) &&
            isPointInsideFrame(frame, point3.x, point3.y) &&
            isPointInsideFrame(frame, point4.x, point4.y)
        ) {
            return true;
        }

        return false;
    }
    iscrossSelectFrame(frame) {
        // Validate frame before proceeding
        if (!frame || !frame.point1 || !frame.point2 || !frame.point3 || !frame.vec) {
            return false;
        }

        let xcross1 = findLinesIntersection(this, frame.vec.getOrtho1X());
        let ycross1 = findLinesIntersection(this, frame.vec.getOrtho1Y());
        let xcross2 = findLinesIntersection(this, frame.vec.getOrtho2X());
        let ycross2 = findLinesIntersection(this, frame.vec.getOrtho2Y());
        if (xcross1 || ycross1 || xcross2 || ycross2 || this.isinSelectFrame(frame)) {
            return true;
        }
        return false;

    }
    isinSelectFrameAtLeast(frame) {
        // Validate frame before proceeding
        if (!frame || !frame.point1 || !frame.point2 || !frame.point3 || !frame.vec) {
            return false;
        }

        var isinFrame = this.isinSelectFrame(frame);

        var vec1 = frame.vec.getOrtho1X();
        var vec2 = frame.vec.getOrtho2X();
        var vec3 = frame.vec.getOrtho1Y();
        var vec4 = frame.vec.getOrtho2Y();

        var crossx1 = findLinesIntersection(this, vec1);
        var crossx2 = findLinesIntersection(this, vec2);
        var crossy1 = findLinesIntersection(this, vec3);
        var crossy2 = findLinesIntersection(this, vec4);

        if (crossx1 || crossx2 || crossy1 || crossy2 || isinFrame) {
            return true;
        }
        return false;


    }

    isinSelectBoundary(mouse) {
        return isinSelectBoundaryLine(mouse, this.start, this.end);
    }

    setSelectBoundary() {

        this.selectBoundary = getLineSelectBoundary(this.start, this.end);
    }
    // --------- MAGNETS ---------
    isinGripStart = (mouse) => this.grip.isin(this.start, mouse);
    isinGripEnd = (mouse) => this.grip.isin(this.end, mouse);
    isinGripMid = (mouse) => this.grip.isin(this.mid, mouse);
    isinTripHstart = (mouse) => this.tripH.isin(this.start, mouse);
    isinTripHmid = (mouse) => this.tripH.isin(this.mid, mouse);
    isinTripHend = (mouse) => this.tripH.isin(this.end, mouse);
    isinTripVstart = (mouse) => this.tripV.isin(this.start, mouse);
    isinTripVmid = (mouse) => this.tripV.isin(this.mid, mouse);
    isinTripVend = (mouse) => this.tripV.isin(this.end, mouse);
    // --------- MAGENTS ---------


    zoom(zl: number) {
        const zoom_mat = mat3.fromScaling(mat3.create(), new Float32Array([zl, zl, 1]));
        
        // Preserve point identities: mutate coordinates instead of replacing instances
        const newStart = transformPointByMatrix3(zoom_mat, this.start);
        const newMid = transformPointByMatrix3(zoom_mat, this.mid);
        const newEnd = transformPointByMatrix3(zoom_mat, this.end);
        
        this.start.x = newStart.x;
        this.start.y = newStart.y;
        this.mid.x = newMid.x;
        this.mid.y = newMid.y;
        this.end.x = newEnd.x;
        this.end.y = newEnd.y;
        // s.tolerance = s.tolerance * zl;
    }

    private copyIds(start: any, mid: any, end: any) {
        var startId = this.start.id;
        var midId = this.mid.id;
        var endId = this.end.id;

        this.start = start;
        this.mid = mid;
        this.end = end;
        this.start.id = startId;
        this.mid.id = midId;
        this.end.id = endId;
    }

    pan(tx: number, ty: number) {
        const pan_mat = mat3.fromTranslation(mat3.create(), new Float32Array([tx, ty, 0]));
        mat3.transpose(pan_mat, pan_mat);
        
        // Preserve point identities: mutate coordinates instead of replacing instances
        const newStart = transformPointByMatrix3(pan_mat, this.start);
        const newMid = transformPointByMatrix3(pan_mat, this.mid);
        const newEnd = transformPointByMatrix3(pan_mat, this.end);
        
        this.start.x = newStart.x;
        this.start.y = newStart.y;
        this.mid.x = newMid.x;
        this.mid.y = newMid.y;
        this.end.x = newEnd.x;
        this.end.y = newEnd.y;
    }
    /**
     * Функция возвращает две точки пересечения, если текущая линия пересекается с другими фигурами.
     * Особенность функции в том, что сначала проверяются все круги, и если точки пересечения найдены, происходит возврат из функции - результат получен.
     * Если точки пересечения с кругами не найдены, таким же образом проверяются все прямоугольники, и также, если точки найдены - возврат из функции.
     * Далее, если ни с кругами, ни с прямоугольниками текущая линия не пересекается, проверяются линии.
     */
    getBreakPoints(mouse, shapes_) {
        let bs, be
        const shapesCircle = shapes_.filter(shape => shape.type === ObjectTypes.circle);
        const shapesLine = shapes_.filter(shape => shape.type === ObjectTypes.line);
        const shapesRectangle = shapes_.filter(shape => shape.type === ObjectTypes.rectangle);

        for (const shape of shapesCircle) {
            if (this.isinCircle(shape, mouse)) {
                const intersectionPoints = this.findCircleLineIntersections(shape, this);
                if (intersectionPoints.length > 0) {
                    if (intersectionPoints.length === 1) {
                        bs = intersectionPoints[0];
                        be = null;
                    }
                    else {
                        for (let point of intersectionPoints) {
                            if (!bs) {
                                bs = point;
                            } else {
                                be = point;
                            }

                        }
                    }

                }
                else {
                    continue;
                }

                return { bs, be }; // Return immediately if circle condition is met

            }
        }
        for (const shape of shapesRectangle) {
            if (this.isinRectangle(shape, mouse)) {
                let intercections = findRectangleAndLineIntersectionPoints(shape.p1, shape.p2, shape.p3, shape.p4, this.start, this.end);
                if (intercections.length > 0) {
                    if (intercections.length === 1) {
                        bs = intercections[0];
                        be = null;
                    }
                    else {
                        for (let point of intercections) {
                            if (!bs) {
                                bs = point;
                            } else {
                                be = point;
                            }
                        }
                    }
                }
                else {
                    continue;
                }
                return { bs, be }; // Return immediately if rectangle condition is met
            }
        }

        let closest = this.findClosest(mouse, shapesLine);
        if (closest.length === 0) {
            return { bs: null, be: null };
        }
        if (closest.length === 1) {
            return { bs: closest[0], be: null }
        }
        else {
            return { bs: closest[0], be: closest[1] }
        }


    }

    isinCircle(circle, point) {
        const dx = point.x - circle.center.x;
        const dy = point.y - circle.center.y;
        return dx * dx + dy * dy < circle.radius * circle.radius;
    }
    /**
     * Функция проверяет, находится ли мышь внутри прямоугольника
     */
    isinRectangle(rectangle, mouse) {
        var [p1, p2, p3, p4] = rectangle.getPoints();

        return pointInsideRectangle(mouse, p1, p2, p3, p4);
    }



    findIntersectionPoint(selectedLine, line2) {
        const x1 = selectedLine.start.x;
        const y1 = selectedLine.start.y;
        const x2 = selectedLine.end.x;
        const y2 = selectedLine.end.y;

        const x3 = line2.start.x;
        const y3 = line2.start.y;
        const x4 = line2.end.x;
        const y4 = line2.end.y;


        const denominator = (y4 - y3) * (x2 - x1) - (x4 - x3) * (y2 - y1);

        if (denominator === 0) {
            return null; // Lines are parallel or coincident
        }

        const ua = ((x4 - x3) * (y1 - y3) - (y4 - y3) * (x1 - x3)) / denominator;
        const ub = ((x2 - x1) * (y1 - y3) - (y2 - y1) * (x1 - x3)) / denominator;

        if (ua >= 0 && ua <= 1 && ub >= 0 && ub <= 1) {
            const x = x1 + ua * (x2 - x1);
            const y = y1 + ua * (y2 - y1);
            return new Point(x, y);
        } else {
            return null; // Intersection point is outside the line segments
        }
    }




    findCircleLineIntersections(circle, line) {
        const circleCenter = circle.center;

        const circleRadius = circle.radius;

        const dx = line.end.x - line.start.x;
        const dy = line.end.y - line.start.y;

        const a = dx * dx + dy * dy;
        const b = 2 * (dx * (line.start.x - circleCenter.x) + dy * (line.start.y - circleCenter.y));
        const c = circleCenter.x * circleCenter.x + circleCenter.y * circleCenter.y + line.start.x * line.start.x + line.start.y * line.start.y - 2 * (circleCenter.x * line.start.x + circleCenter.y * line.start.y) - circleRadius * circleRadius;

        const discriminant = b * b - 4 * a * c;

        if (discriminant < 0) {
            return []; // No intersection, return empty array
        } else {
            // Two intersection points
            const t1 = (-b + Math.sqrt(discriminant)) / (2 * a);
            const t2 = (-b - Math.sqrt(discriminant)) / (2 * a);
            const intersection1 = new Point(line.start.x + t1 * dx, line.start.y + t1 * dy);
            const intersection2 = new Point(line.start.x + t2 * dx, line.start.y + t2 * dy);
            if (this.isinCircle(circle, line.start) || this.isinCircle(circle, line.end)) {
                if (this.isinCircle(circle, line.start)) {
                    const side = getSideOfMouseRelativeToLine(line.start, intersection1, line);
                    if (side === 'start') {
                        return [intersection1];
                    }
                    else {
                        return [intersection2];
                    }

                }
                else if (this.isinCircle(circle, line.end)) {
                    const side = getSideOfMouseRelativeToLine(line.end, intersection1, line);
                    if (side === 'start') {
                        return [intersection1];
                    }
                    else {
                        return [intersection2];
                    }
                }
            }
            else {
                return [intersection1, intersection2];

            }
        }
    }


    doLinesIntersect(line1, line2) {
        const x1 = line1.start.x;
        const y1 = line1.start.y;
        const x2 = line1.end.x;
        const y2 = line1.end.y;

        const x3 = line2.start.x;
        const y3 = line2.start.y;
        const x4 = line2.end.x;
        const y4 = line2.end.y;

        const denominator = (y4 - y3) * (x2 - x1) - (x4 - x3) * (y2 - y1);
        if (denominator === 0) {
            return false; // Lines are parallel
        }

        const ua = ((x4 - x3) * (y1 - y3) - (y4 - y3) * (x1 - x3)) / denominator;
        const ub = ((x2 - x1) * (y1 - y3) - (y2 - y1) * (x1 - x3)) / denominator;

        return ua >= 0 && ua <= 1 && ub >= 0 && ub <= 1;
    }

    findClosest(mouse, shapes) {
        const mouseProjection = getProjection(mouse, this);
        const intersections = shapes.map(shape => {
            return this.findIntersectionPoint(this, shape);
        }).filter(item => item);
        const closestPoints = findClosestPoints(mouseProjection, intersections);
        return closestPoints;
    }

    calculateDistance(mouse, line) {
        const result = Math.hypot(mouse.x - line.start.x, mouse.y - line.start.y)
        return result;
    }

    isEqual(anotherLine: Line) {
        if (this.start.equals(anotherLine.start) && this.end.equals(anotherLine.end)) {
            return true;
        }
        return false;
    }

    getLength() {
        const dx = this.end.x - this.start.x;
        const dy = this.end.y - this.start.y;
        return Math.sqrt(dx * dx + dy * dy);
    }

    getOpenEnd() {
        if ((this.startJ.id && this.endJ.id) || (!this.startJ.id && !this.endJ.id)) {
            return null;
        }
        else if (!this.startJ.id) {
            return this.startJ;
        }
        else {
            return this.endJ;
        }
    }

    /**
     * Методы трансформации - если ни один из этих методов не выполнен,
     * то this.networkInfo будет undefined, что означает, что линия является просто линией - ни воздуховодом, ни трубой
     */
    makeRectDuct(widthMIL, heightMIL, flowRateCMH) {
        this.net = new NetworkInfo(ObjectTypes.duct, [widthMIL, heightMIL], flowRateCMH, null, undefined, null, null);
    }
    makeRoundDuct(diameterMIL, flowRateCMH) {
        this.net = new NetworkInfo(ductOrPipe.duct, diameterMIL, flowRateCMH, null, undefined, null, null);
    }
    makePipe(diameterMIL, flowRateLH) {
        // подразумевается, что трубы только круглые
        this.net = new NetworkInfo(ductOrPipe.pipe, diameterMIL, flowRateLH, null, null, null, null);
    }

    /**
     * Этот метод вызывается только когда прямо или косвенно (введением параметров сети в ObjectProperties)
     * принято решение, что линия будет представлять трубу или воздуховод. В клонировании линии (именно линии как линии)
     * такие объекты, указывающие на более специфичное назначение создаваемой линии, не участвуют.
     * То есть объект NetworkInfo нужно присоединить где-то в рабочем потоке программы, когда
     * уже известно, что линия подразумевает трубу или воздуховод
     */
    initNetworkInfo() {
        if (!this.net) {
            this.net = new NetworkInfo(null, null, null, this.getLength(), undefined, null, null);
        }
    }
    getAngleBetween(segment) {
        var vec1 = new Vector(this.start, this.end);
        var vec2 = new Vector(segment.start, segment.end);
        var vecMod1 = Math.hypot(vec1.xc, vec1.yc);
        var vecMod2 = Math.hypot(vec2.xc, vec2.yc);
        var dotProduct = (vec1.xc * vec2.xc) + (vec1.yc * vec2.yc);
        var cosA = dotProduct / (vecMod1 * vecMod2);
        var angle = Math.acos(cosA);
        var angleDeg = angle * (180 / Math.PI);

        return angleDeg;
    }

    getAngleDeg() {
        var vec1 = new Vector(this.start, this.end);
        var vec2 = new Vector(Point.ORIGIN, new Point(100, 0));
        var vecMod1 = Math.hypot(vec1.xc, vec1.yc);
        var vecMod2 = Math.hypot(vec2.xc, vec2.yc);
        var dotProduct = (vec1.xc * vec2.xc) + (vec1.yc * vec2.yc);
        var cosA = dotProduct / (vecMod1 * vecMod2);
        var angle = Math.acos(cosA);
        var angleDeg = angle * (180 / Math.PI);


        return angleDeg;

    }

    getObject() {
        let json = JSON.stringify(this);
        let result = JSON.parse(json);
        result.id = super.id;
        result.joinedStart = this.joinedStart;
        result.joinedEnd = this.joinedEnd;
        result.thickness = this.thickness;
        return result;
    }

    getConnectedSegments() {
        var result = getDisplayedShapes()
            .filter((s: Shape) => s instanceof Line)
            .filter((seg: Line) => {
                // Check if the start point of the current line matches either the start or end point of another segment
                if (this.start.equals(seg.start) || this.start.equals(seg.end)) {
                    return true;
                }
                // Check if the end point of the current line matches either the start or end point of another segment
                if (this.end.equals(seg.start) || this.end.equals(seg.end)) {
                    return true;
                }
                return false;
            });

        return result;
    }

    getOtherPoint(basePoint: Point): Point {
        if (isin(this.start, basePoint)) {
            return this.end;
        }
        else if (isin(this.end, basePoint)) {
            return this.start;
        }
        else {
            return this.start;
        }
    }

    getVector(basePoint: Point): Vector {
        var otherPoint = this.getOtherPoint(basePoint);
        return new Vector(basePoint, otherPoint);
    }

    breakMe(start: Point, end: Point): Line[] {
        var result = [];

        var len1 = getDistance(this.start, start);
        var len2 = getDistance(this.start, end);


        var clone1 = this.getClone();
        var clone2 = this.getClone();

        if (len1 > len2) {

            clone1.end = end;
            clone2.start = start;
        }
        else if (len1 < len2) {

            clone1.end = start;
            clone2.start = end;
        }

        result.push(clone1);
        result.push(clone2);

        return result;
    }

    getConnectedElbows() {
        var elbows: ElbowBlock[] = (getDisplayedShapes().filter((s: Shape) => s instanceof ElbowBlock) as ElbowBlock[]);

        var connectedElbows = elbows.filter((elb: ElbowBlock) => {
            let cpoints = elb.getCpoints();

            let cp1 = cpoints.cp1;
            let cp2 = cpoints.cp2;

            if (isin(cp1, this.start) || isin(cp1, this.end) || isin(cp2, this.start) || isin(cp2, this.end)) {
                return true;
            }
            return false;
        });

        return connectedElbows;
    }

    getWhichEnd(point: Point): 'start' | 'end' {
        if (isin(point, this.start)) return 'start';
        else if (isin(point, this.end)) return 'end';
        else return null;
    }

    /**
     * Функция проверяет, на каком конце отрезка нет отвода
     */
    whichEndHasNoElbow() {
        var result = [];
        getDisplayedShapes().filter(s => s instanceof ElbowBlock)
            .forEach((elb: ElbowBlock) => {
                if (isin(this.start, elb.base) || isin(this.start, elb.side)) {
                    result.push('start');
                }
                if (isin(this.end, elb.base) || (isin(this.end, elb.side))) {
                    result.push('end');
                }
            });
        

        if (result.includes('start') && result.includes('end')) {
            return null;
        }
        else if (result.includes('start')) {
            return 'end';
        }
        else if (result.includes('end')) {
            return 'start';
        }
    }

    reset() {
        this.start = Point.ORIGIN.clone();
        this.end = Point.ORIGIN.clone();
        this.routeId = null;
    }

    /**
     * Функция возвращает объект crucialPoints, который используется для определения маршрутов
     */
    getCrucialPoints(): RouteCrucialPoints{
        return {
            start: this.start,
            end: this.end
        }
    }

    getCrucialPointsArray(): Point[] {
        const { start, mid, end } = this.getCrucialPoints();
        const points: Point[] = [];

        if (start) {
            points.push(start);
        }

        if (end) {
            points.push(end);
        }

        if (mid) {
            points.push(mid);
        }

        return points;
    }

}